// API Response 基礎類型
export interface ApiResponse<T> {
  code: number;
  msg: string;
  data: T;
}

// Presigned Upload URL 回應
export interface PresignResponse {
  upload_url: string;
  session_id: string;
  object_path: string;
  expires_at: string;
}

// 下載 URL 回應（新版）
export interface DownloadUrlResponse {
  download_url: string;
  file_name: string;
  file_size: number;
  content_type: string;
  expires_at: string;
}

export type UploadStatus = "pending" | "processing" | "completed" | "failed";

export interface FileInfo {
  name: string;
  size: number;
}

export interface CreateItemData {
  id: string;
  unlock_url: string;
  file_count: number;
  total_size: number;
  upload_status: UploadStatus;
}

export interface ItemData {
  id: string;
  title: string;
  description: string;
  total_file_count: number;
  total_file_size: number;
  files?: FileInfo[];
  price: string;
  currency: string;
  valid_from: string;
  valid_until: string;
  status: "locked" | "unlocked" | "expired";
  is_valid: boolean;
  unlocked_at: string | null;
  upload_status: UploadStatus;
  upload_error?: string;
}

// 買家 API 類型 - EIP-5792 + UnlockPayment 分潤合約

export interface Split {
  to: string;
  bps: number;
}

export interface PaymentParams {
  item_id: string;
  amount: string;
  splits: Split[];
  deadline: number;
  nonce: string;
}

export interface Eip2612Domain {
  name: string;
  version: string;
  chainId: number;
  verifyingContract: string;
}

export interface Eip2612Message {
  owner: string;
  spender: string;
  value: string;
  nonce: number;
  deadline: number;
}

export interface Eip2612Data {
  domain: Eip2612Domain;
  types: Record<string, Array<{ name: string; type: string }>>;
  message: Eip2612Message;
}

export interface PermitResponse {
  intent_id: string;
  payment_params: PaymentParams;
  backend_signature: string;
  eip2612: Eip2612Data;
  contract_address: string;
  chain_id: number;
  item_uuid: string;
  seller_address: string;
}

// CDP Webhook Intent 狀態
export type IntentStatus = "pending" | "confirmed" | "completed" | "expired";

export interface IntentStatusData {
  intent_id: string;
  status: IntentStatus;
  download_token?: string;  // 僅 completed 時
  expires_at?: string;      // 僅 completed 時
}

export interface DownloadTokenRequest {
  wallet_address: string;
  signature: string;
  message: string;
  timestamp: number;
}

export interface DownloadTokenData {
  download_token: string;
  expires_at: string;
}

// 錯誤碼
export const ErrorCodes = {
  SUCCESS: 0,
  // Client 錯誤
  ROUTE_NOT_FOUND: 809101001,
  INVALID_REQUEST: 809101002,
  MISSING_AUTH: 809101003,
  INVALID_AUTH: 809101004,
  ACCESS_DENIED: 809101005,
  // Server 錯誤
  INTERNAL_PANIC: 809102001,
  INTERNAL_ERROR: 809102002,
  FILE_UPLOAD_FAILED: 809102003,
  // 業務邏輯錯誤
  RESOURCE_NOT_FOUND: 809103001,
  ITEM_NOT_FOUND: 809103002,
  ITEM_EXPIRED: 809103003,
  ITEM_NOT_STARTED: 809103004,
  WALLET_NOT_UNLOCKED: 809103005,
  TRANSACTION_INVALID: 809103006,
  PAYMENT_AMOUNT_MISMATCH: 809103007,
  DOWNLOAD_TOKEN_EXPIRED: 809103008,
  DOWNLOAD_TOKEN_USED: 809103009,
  DOWNLOAD_TOKEN_NOT_FOUND: 809103010,
  TRANSACTION_ALREADY_USED: 809103011,
  WALLET_ALREADY_UNLOCKED: 809103012,
  CHAIN_ID_MISMATCH: 809103013,
  WALLET_SIGNATURE_INVALID: 809103014,
  ITEM_INVALID: 809103015,
  UPLOAD_PENDING: 809103016,
  UPLOAD_PROCESSING: 809103017,
  PRICE_EXCEEDS_MAX: 809103018,
  // Presigned URL 相關錯誤
  UPLOAD_SESSION_NOT_FOUND: 809104001,
  UPLOAD_SESSION_EXPIRED: 809104002,
  FILE_NOT_FOUND_R2: 809104003,
  RATE_LIMIT_EXCEEDED: 809104004,
  // Terms 相關錯誤
  TERMS_NOT_ACCEPTED: 809105001,
  // Intent 相關錯誤（CDP Webhook 流程）
  INTENT_NOT_FOUND: 809105002,
  // Turnstile 相關錯誤
  TURNSTILE_VERIFICATION_FAILED: 809106001,
  TURNSTILE_TOKEN_MISSING: 809106002,
  // Auth 相關錯誤
  AUTH_INVALID_TOKEN: 809107001,
  AUTH_TOKEN_EXPIRED: 809107002,
  AUTH_REFRESH_FAILED: 809107003,
  // API Key 相關錯誤
  API_KEY_ALREADY_EXISTS: 809108004,
  API_KEY_NOT_FOUND: 809108007,
  // Coupon 相關錯誤
  COUPON_NOT_FOUND: 809109001,
  COUPON_NOT_REDEEMABLE: 809109002,
  COUPON_ALREADY_REDEEMED: 809109003,
  COUPON_ALREADY_ACTIVE: 809109004,
} as const;

// Terms Types
export type TermsType = "TOS" | "BUYER_DISCLAIMER" | "SELLER_DISCLAIMER";

// Auth Types
export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  avatar_url?: string;
  created_at: string;
}

export interface GoogleAuthResponse {
  user: AuthUser;
}

export interface TermsStatus {
  tos: boolean;
  buyer_disclaimer: boolean;
  seller_disclaimer: boolean;
}

export interface AcceptTermsResponse {
  accepted_at: string;
}

// Seller Dashboard Types
export interface SellerItem {
  id: string;
  title: string;
  description: string;
  price: string;
  currency: string;
  valid_from: string;
  valid_until: string;
  status: "active" | "expired";
  sales_count: number;
  revenue: string;
  created_at: string;
  unlock_url: string;
}

export interface SellerStats {
  total_revenue: string;
  total_sales: number;
  active_items: number;
  total_items: number;
}

export interface SellerItemsResponse {
  items: SellerItem[];
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface GetSellerItemsParams {
  page?: number;
  limit?: number;
  status?: "all" | "active" | "expired";
}

export interface SellerSettings {
  default_payout_address: string;
}

// Coupon Types
export interface RedeemCouponResponse {
  platform_fee_percent: number;
  expires_at: string;
  redeemed_at: string;
}

export interface ActiveCouponResponse {
  has_active_coupon: boolean;
  platform_fee_percent?: number;
  expires_at?: string;
  current_fee_percent: number;
}

// API Key Types
export interface ApiKeyInfo {
  id: string;
  name: string;
  key_prefix: string;
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
}

export interface ApiKeyCreated {
  id: string;
  name: string;
  key: string;
  key_prefix: string;
  created_at: string;
}
